import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Employee income report — the current user's own commission/KPI income rows and
 * summary stats. Mirrors datacentral-client-v4
 * `modules/Report/components/EmployeeIncome/services`. Access is department-gated
 * in the UI (BOD / BOM / SALES / ACCOUNT); the API also enforces scope.
 */

/** Departments allowed to see "My incomes" (matches legacy whitelist). */
export const INCOME_ALLOWED_DEPARTMENTS = [
  'BOARD OF DIRECTORS',
  'BOARD OF MANAGEMENT',
  'SALES',
  'ACCOUNT',
];

export interface Income {
  customer_name: string;
  service_id: number;
  service_name: string;
  service_type: string;
  product_group_name: string;
  invoice_id: number;
  invoice_item_amount: number;
  /** "MM/YYYY"-ish month string. */
  month_created_invoice: string;
  payment_date: string;
  kpi_type: string;
  /** Fraction (multiply by 100 for %). */
  kpi_rate: number;
  kpi_amount: number;
}

export interface IncomeStatItem {
  name?: string;
  label?: string;
  amount?: number;
  value?: number;
}

export interface IncomeStats {
  top_employees: IncomeStatItem[];
  top_product_groups: IncomeStatItem[];
  top_services: IncomeStatItem[];
  top_customers: IncomeStatItem[];
  total_amount: number;
  total_invoice_items: number;
}

export interface IncomeQuery {
  q?: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  /**
   * JSON-encoded filter blob sent as `filters=` query param.
   * Expected shape: `{"gte":"YYYY-MM-DD","lte":"YYYY-MM-DD"}`.
   * The API parses it server-side via util.ParseFilters[EmployeeIncomesQueriesExport].
   */
  filters?: string;
}

function toQuery(params: IncomeQuery): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') q.set(k, String(v));
  }
  return q.toString();
}

export function useEmployeeIncomes(params: IncomeQuery = {}) {
  return useQuery({
    queryKey: ['account', 'incomes', params],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await dataCentralApi.get<ResponseArray<Income>>(
        `/reports/employees/incomes?${toQuery({ page: 1, page_size: 20, ...params })}`,
      );
      const data = res.data as ResponseArray<Income> & { result?: Income[] };
      return { results: data.results ?? data.result ?? [], pagination: data.pagination };
    },
  });
}

export function useEmployeeIncomeStats(params: Pick<IncomeQuery, 'q' | 'filters'> = {}) {
  return useQuery({
    queryKey: ['account', 'incomes', 'stats', params],
    queryFn: async (): Promise<IncomeStats | null> => {
      const res = await dataCentralApi.get<ResponseObject<IncomeStats>>(
        `/reports/employees/incomes/stats?${toQuery(params)}`,
      );
      return res.data.result ?? null;
    },
  });
}
